<template>
  <div class="d-flex align-center ga-2 historique-toggle-wrap">
    <v-btn
      :color="btnColor"
      :variant="btnVariant"
      size="small"
      :class="['historique-toggle', { 'historique-toggle--on-dark': onDark, 'historique-toggle--active': isActive }]"
      prepend-icon="mdi-history"
      @click="openModal"
    >
      Historique
      <v-tooltip activator="parent" location="bottom">
        {{ tooltipText }}
      </v-tooltip>
    </v-btn>

    <v-chip
      v-if="isActive && label"
      size="small"
      :color="onDark ? 'white' : 'primary'"
      :variant="onDark ? 'flat' : 'tonal'"
      closable
      class="historique-chip"
      @click:close="clearHistorique"
    >
      {{ label }}
    </v-chip>

    <CommonHistoriqueAnneeModal
      v-model="modalOpen"
      @changed="onChanged"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useHistoriqueMode } from '@/composables/useHistoriqueMode'

const props = withDefaults(defineProps<{
  /** Conservé pour rétrocompatibilité avec v-model sur les pages existantes */
  modelValue?: boolean
  onDark?: boolean
}>(), {
  modelValue: undefined,
  onDark: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const {
  mode,
  enabled,
  label,
  modalOpen,
  setOff,
  openModal,
} = useHistoriqueMode()

const isActive = computed(() => enabled.value)

const tooltipText = computed(() => {
  if (mode.value === 'all') return 'Tout l\'historique — cliquer pour changer'
  if (mode.value === 'annee') return `Année ${label.value} — cliquer pour changer`
  return 'Afficher aussi les sessions terminées / annulées (tout ou par année)'
})

const btnColor = computed(() => {
  if (props.onDark) return 'white'
  return isActive.value ? 'primary' : undefined
})

const btnVariant = computed(() => {
  if (props.onDark) return isActive.value ? 'flat' : 'outlined'
  return isActive.value ? 'tonal' : 'outlined'
})

watch(
  enabled,
  (v) => {
    if (props.modelValue !== undefined && props.modelValue !== v) {
      emit('update:modelValue', v)
    }
  },
  { immediate: true }
)

watch(
  () => props.modelValue,
  (v) => {
    if (v === undefined) return
    if (v && !enabled.value) {
      // Ouverture via v-model=true externe : ouvrir la modale plutôt que forcer "all"
      openModal()
    } else if (v === false && enabled.value) {
      setOff()
    }
  }
)

function clearHistorique() {
  setOff()
  emit('update:modelValue', false)
}

function onChanged() {
  emit('update:modelValue', enabled.value)
}
</script>

<style scoped>
.historique-toggle-wrap {
  flex-shrink: 0;
  flex-wrap: wrap;
}

.historique-toggle {
  text-transform: none;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
}

.historique-chip {
  max-width: 220px;
}

.historique-toggle--on-dark {
  color: #fff !important;
  border-color: rgba(255, 255, 255, 0.85) !important;
}

.historique-toggle--on-dark.historique-toggle--active {
  background: #fff !important;
  color: rgb(0, 68, 85) !important;
  border-color: #fff !important;
}

.historique-toggle--on-dark.historique-toggle--active :deep(.v-icon),
.historique-toggle--on-dark.historique-toggle--active :deep(.v-btn__content) {
  color: rgb(0, 68, 85) !important;
}

.historique-toggle--on-dark:not(.historique-toggle--active) :deep(.v-icon),
.historique-toggle--on-dark:not(.historique-toggle--active) :deep(.v-btn__content) {
  color: #fff !important;
}
</style>
